/**
 * @license
 * Copyright Paysafe 2026. All rights reserved.
 */

import { CommonModule } from '@angular/common';
import { Component, DestroyRef, Input, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PsButtonModule } from '@paysafe-ui/components/button';
import { combineLatest, Observable } from 'rxjs';

import { DEFAULT_ERROR_ILLUSTRATION, ERROR_ILLUSTRATION_MAP } from '../../core/constants/checkout-error-maps.const';
import { CheckoutErrorType } from '../../core/enums/checkout-error-type.enum';
import { ErrorState } from '../../models/ui/error-state.model';
import { CheckoutErrorHandlerService } from '../../services/error-handler/checkout-error-handler.service';
import { UIStateService } from '../../services/ui-state/ui-state.service';

@Component({
  selector: 'app-checkout-error',
  imports: [CommonModule, PsButtonModule, TranslateModule],
  templateUrl: './checkout-error.component.html',
  styleUrl: './checkout-error.component.scss',
  standalone: true
})
export class CheckoutErrorComponent implements OnInit {

  illustration = '';
  title = '';
  subtitle = '';
  primaryButtonText = '';
  secondaryButtonText = '';
  hasPrimaryButton = false;
  hasSecondaryButton = false;

  @Input() primaryActionOverride?: () => void;

  private retryPath = '/';
  private isRetryable = false;
  private primaryAction?: () => void;
  private primaryActionUrl?: string;
  private secondaryActionUrl?: string;
  private lastErrorState: ErrorState | null = null;

  constructor(
    private uiStateService: UIStateService,
    private translateService: TranslateService,
    private router: Router,
    private destroyRef: DestroyRef,
    private checkoutErrorHandler: CheckoutErrorHandlerService
  ) {
    // Initializer, nothing to do.
  }

  ngOnInit(): void {
    this.uiStateService.errorState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(errorState => {
        this.lastErrorState = errorState;
        this.updateComponentState(errorState);
      });

    // Re-apply translations when they load (handles async loading)
    this.translateService.onTranslationChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.lastErrorState) {
          this.updateComponentState(this.lastErrorState);
        }
      });

    this.destroyRef.onDestroy(() => {
      this.uiStateService.clearError();
    });
  }

  private updateComponentState(errorState: ErrorState): void {
    const type = errorState.type || CheckoutErrorType.GENERAL;
    const metadata = errorState.metadata;
    const i18nKey = `CHECKOUT.ERROR.${type}`;

    this.illustration = ERROR_ILLUSTRATION_MAP[type] || DEFAULT_ERROR_ILLUSTRATION;
    this.isRetryable = errorState.isRetryable;
    this.retryPath = errorState.path || '/';
    this.primaryAction = metadata?.primaryAction;
    this.primaryActionUrl = metadata?.primaryActionUrl;
    this.secondaryActionUrl = metadata?.secondaryActionUrl;

    const title$ = metadata?.title
      ? new Observable<string>(sub => {
          sub.next(metadata.title);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.TITLE`);

    const description$ = metadata?.subtitle
      ? new Observable<string>(sub => {
          sub.next(metadata.subtitle);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.DESCRIPTION`);

    const primaryBtn$ = this.isRetryable && metadata?.retryText
      ? new Observable<string>(sub => {
          sub.next(metadata.retryText);
          sub.complete();
        })
      : this.translateService.get(`${i18nKey}.PRIMARY_BUTTON`);

    const secondaryBtn$ = this.translateService.get(`${i18nKey}.SECONDARY_BUTTON`);

    combineLatest([
      title$,
      description$,
      primaryBtn$,
      secondaryBtn$
    ] as [Observable<string>, Observable<string>, Observable<string>, Observable<string>])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([title, description, primaryBtn, secondaryBtn]) => {
        this.title = title;
        this.subtitle = description;
        this.primaryButtonText = primaryBtn;
        this.hasPrimaryButton = true;

        if (metadata?.primaryAction || metadata?.primaryActionUrl || metadata?.primaryActionText) {
          this.primaryButtonText = metadata.primaryActionText || primaryBtn;
        }

        const hasValidSecondaryButton = !!secondaryBtn && secondaryBtn.trim() !== '';
        this.hasSecondaryButton = hasValidSecondaryButton;
        this.secondaryButtonText = hasValidSecondaryButton ? secondaryBtn : '';
      });
  }

  handlePrimaryAction(): void {
    if (this.primaryActionOverride) {
      this.primaryActionOverride();
      return;
    }

    if (this.isRetryable) {
      this.navigateToRetryPath();
      return;
    }

    if (this.primaryAction) {
      this.primaryAction();
      return;
    }

    if (this.primaryActionUrl) {
      window.open(this.primaryActionUrl, '_blank');
      return;
    }

    window.location.href = this.checkoutErrorHandler.getStoredReturnUrl() ?? '/';
  }

  handleSecondaryAction(): void {
    window.open(this.secondaryActionUrl ?? '/');
  }

  private navigateToRetryPath(): void {
    const urlTree = this.router.parseUrl(this.retryPath);
    this.router.navigateByUrl(urlTree);
  }
}
